import type { CSSProperties } from 'react';
import { ApiReferenceReact } from '@scalar/api-reference-react';
import { ArrowRight, Bot, Webhook, Zap } from 'lucide-react';
import { useNavigation } from '@/app/navigation/use-navigation';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ??
  'https://yodnouwloaabmhkffdpl.supabase.co/functions/v1/api';

const OPENAPI_URL = `${API_BASE_URL}/v1/openapi.json`;

interface ApiDocsPageProps {
  onBack: () => void;
}

interface IntegrationLogo {
  name: string;
  src: string;
}

// Static favicons fetched from Google's s2 service (see public/integrations/).
const INTEGRATIONS: IntegrationLogo[] = [
  { name: 'Claude', src: '/integrations/anthropic.png' },
  { name: 'Cursor', src: '/integrations/cursor.png' },
  { name: 'Zapier', src: '/integrations/zapier.png' },
  { name: 'Make', src: '/integrations/make.png' },
  { name: 'n8n', src: '/integrations/n8n.png' },
  { name: 'Bling', src: '/integrations/bling.png' },
  { name: 'Omie', src: '/integrations/omie.png' },
  { name: 'RD Station', src: '/integrations/rdstation.png' },
];

const PAGE_STYLE: CSSProperties = {
  flex: 1,
  background: 'var(--panel)',
  borderRadius: '20px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
};

const HERO_STYLE: CSSProperties = {
  background: 'linear-gradient(135deg, #154680 0%, #1e40af 60%, #3730a3 100%)',
  color: '#fff',
  padding: '40px 32px',
  borderRadius: '16px 16px 0 0',
  boxShadow: '0 10px 30px rgba(21, 70, 128, 0.25)',
  position: 'relative',
  overflow: 'hidden',
};

const HERO_INNER_STYLE: CSSProperties = {
  maxWidth: '720px',
  margin: '0 auto',
  textAlign: 'center',
  position: 'relative',
  zIndex: 1,
};

const BETA_BADGE_STYLE: CSSProperties = {
  display: 'inline-block',
  padding: '4px 12px',
  background: 'rgba(255, 255, 255, 0.18)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  borderRadius: '999px',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  marginBottom: '16px',
};

const HERO_TITLE_STYLE: CSSProperties = {
  margin: '0 0 12px',
  fontSize: '32px',
  fontWeight: 700,
  lineHeight: 1.15,
  letterSpacing: '-0.5px',
};

const HERO_SUBTITLE_STYLE: CSSProperties = {
  margin: '0 0 28px',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: 1.5,
  color: 'rgba(255, 255, 255, 0.88)',
};

const HERO_FEATURES_STYLE: CSSProperties = {
  display: 'flex',
  gap: '24px',
  justifyContent: 'center',
  flexWrap: 'wrap',
  marginBottom: '28px',
  fontSize: '13px',
  color: 'rgba(255, 255, 255, 0.85)',
};

const HERO_FEATURE_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const CTA_BUTTON_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  background: '#fff',
  color: '#154680',
  padding: '12px 24px',
  borderRadius: '8px',
  border: 'none',
  fontWeight: 600,
  fontSize: '14px',
  cursor: 'pointer',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
};

const LOGOS_SECTION_STYLE: CSSProperties = {
  padding: '20px 32px',
  background: '#f9fafb',
  borderBottom: '1px solid #e5e7eb',
};

const LOGOS_LABEL_STYLE: CSSProperties = {
  textAlign: 'center',
  margin: '0 0 16px',
  fontSize: '11px',
  fontWeight: 600,
  color: '#6b7280',
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
};

const LOGOS_GRID_STYLE: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
  gap: '8px',
  alignItems: 'center',
};

const LOGO_TILE_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 8px',
  borderRadius: '8px',
  background: '#fff',
  border: '1px solid #e5e7eb',
  fontSize: '12px',
  fontWeight: 500,
  color: '#374151',
};

const LOGO_IMG_STYLE: CSSProperties = {
  width: '32px',
  height: '32px',
  objectFit: 'contain',
};

const REFERENCE_WRAPPER_STYLE: CSSProperties = {
  flex: 1,
  minHeight: '600px',
  width: '100%',
  overflow: 'hidden',
};

export function ApiDocsPage({ onBack: _onBack }: ApiDocsPageProps) {
  const { actions } = useNavigation();

  return (
    <div style={PAGE_STYLE}>
      <section style={HERO_STYLE}>
        <div style={HERO_INNER_STYLE}>
          <span style={BETA_BADGE_STYLE}>Beta · v1.0</span>
          <h1 style={HERO_TITLE_STYLE}>Conecte o Joshua a qualquer ferramenta</h1>
          <p style={HERO_SUBTITLE_STYLE}>
            API REST com 29 endpoints, webhooks de eventos em tempo real e suporte
            nativo ao protocolo MCP — para que pessoas, obras, atividades e ordens de
            serviço fluam entre o Joshua e o resto do seu stack.
          </p>

          <div style={HERO_FEATURES_STYLE}>
            <span style={HERO_FEATURE_STYLE}>
              <Zap size={14} />
              REST + OpenAPI 3.1
            </span>
            <span style={HERO_FEATURE_STYLE}>
              <Webhook size={14} />
              Webhooks com HMAC
            </span>
            <span style={HERO_FEATURE_STYLE}>
              <Bot size={14} />
              MCP server oficial
            </span>
          </div>

          <button type="button" style={CTA_BUTTON_STYLE} onClick={() => actions.navigate('api-keys')}>
            Gerar uma chave de API
            <ArrowRight size={16} />
          </button>
        </div>
      </section>

      <section style={LOGOS_SECTION_STYLE}>
        <p style={LOGOS_LABEL_STYLE}>Funciona com</p>
        <div style={LOGOS_GRID_STYLE}>
          {INTEGRATIONS.map((logo) => (
            <div key={logo.name} style={LOGO_TILE_STYLE} title={logo.name}>
              <img src={logo.src} alt={`${logo.name} logo`} style={LOGO_IMG_STYLE} />
              {logo.name}
            </div>
          ))}
        </div>
      </section>

      <div style={REFERENCE_WRAPPER_STYLE}>
        <ApiReferenceReact
          configuration={{
            url: OPENAPI_URL,
            theme: 'default',
            hideClientButton: false,
            hideDarkModeToggle: false,
          }}
        />
      </div>
    </div>
  );
}

export default ApiDocsPage;
